/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosInstance, AxiosPromise, AxiosRequestConfig } from 'axios';

import { getMobileOperatingSystem } from 'common/helpers/utils';

import i18n from '../config/i18n';
import EncodingService from './encoding-service';
import { trackError } from './tracker-service';
import FingerprintService from './trackers/fingerprint';
import SingularTracker from './trackers/singular';

export const BASE_URL = process.env.REACT_APP_API_BASE_URL;
export const gatewayBaseURL = process.env.REACT_APP_GATEWAY_BASE_URL;
export interface ApiResponse<T = any> {
  data: T;
  hasError?: boolean;
  error?: string;
  headers?: T;
}

interface AxiosErrorJson {
  code?: number;
  columnNumber?: number;
  config: {
    url: string;
    method: string;
    data: any;
    headers: { [key: string]: string };
    baseURL: string;
  };
  description?: string;
  fileName?: string;
  lineNumber?: number;
  message: string;
  name: string;
  number?: number;
  stack: string;
}

export class HttpClient {
  instance: AxiosInstance | any = null;

  constructor(gateway = false) {
    if (this.instance === null) {
      this.instance = axios.create({
        baseURL: !gateway ? BASE_URL : gatewayBaseURL,
        withCredentials: true,
      });
    }
    (this.instance as AxiosInstance).interceptors.request.use(
      (request) => {
        const lang = i18n.language ?? navigator.language.toLowerCase();
        const os = getMobileOperatingSystem();

        request.headers.common['Accept-Language'] = EncodingService.handleEncoding(
          lang.substring(0, 2) || 'en',
        );
        request.headers.common['platform'] = EncodingService.handleEncoding(
          `WEB${os !== 'UNKNOWN' ? '_' + os : ''}`,
        );
        request.headers.common['appname'] = EncodingService.handleEncoding('OT');

        if (window.amp) {
          request.headers.common['AMP-Device-ID'] = EncodingService.handleEncoding(
            window.amp.deviceId,
          );
          request.headers.common['AMP-Session-ID'] = EncodingService.handleEncoding(
            window.amp.sessionId,
          );
        }

        if (!gateway) {
          request.headers.common['appversion'] = EncodingService.handleEncoding(
            process.env.REACT_APP_VERSION ?? '3.3.190',
          );

          if (window.kochava) {
            request.headers.common['Kochava-Device-ID'] = EncodingService.handleEncoding(
              window.kochava.kvID,
            );
          }

          //Fingerprint
          if (FingerprintService.initializated()) {
            request.headers.common['Visitor-ID'] = FingerprintService.getVisitorId();
          }

          request.headers.common['Singular-ID'] =
            SingularTracker.singularDeviceId !== null
              ? SingularTracker.singularDeviceId
              : undefined;
        }

        return request;
      },
      (error) => {
        console.error('Error creating request', error);
        return Promise.reject(error);
      },
    );
    this.instance.interceptors.response.use(
      (response: any) => {
        return {
          data: response.data,
          hasError: false,
          headers: response.headers,
        } as ApiResponse<any>;
      },
      (error: AxiosError): ApiResponse<Error> => {
        try {
          const jsonError = error.toJSON() as AxiosErrorJson;
          if (jsonError.message === 'Network Error') {
            return {
              data: {
                name: 'Possible CORs',
                message: jsonError.message,
                stack: jsonError.stack,
              },
              hasError: true,
              error: JSON.stringify({
                ...jsonError,
                url: `${error.config.baseURL}${error.config.url}`,
              }),
            };
          }
        } catch {}
        const {
          response = {
            status: 400,
            data: { error: 'Request error', description: JSON.stringify(error), status: 500 },
            headers: error && error.config?.headers,
          },
          request = { responseURL: '' },
        } = error;
        const { status, data, headers } = response;
        const { responseURL: url } = request;
        if (status >= 500) {
          trackError(error);
        }
        return { data: { ...data, status, url }, hasError: true, headers: headers };
      },
    );
  }

  static get logoutStatus() {
    return [
      401, // Unauthorized
      // 400, // Bad Request
    ];
  }

  static get warningStatus() {
    return [412];
  }

  resolveResponse<T = any>(promise: ApiResponse<T | Error>, getHeaders?: boolean) {
    return new Promise<T>(async (resolve, reject) => {
      const response = await promise;
      const { data, headers } = response as any;

      if (response.hasError) {
        const error = getHeaders ? { ...data, headers: headers } : data;
        reject(error);
      }

      if (getHeaders) {
        resolve({ data: data, headers: headers } as any);
      }

      resolve(data as T);
    });
  }

  request<T = any>(options: AxiosRequestConfig): Promise<T> {
    return this.resolveResponse<T>(this.instance.request(options));
  }

  get<T = any>(
    url: string,
    params: any = {},
    config: AxiosRequestConfig = {},
    withHeaders?: boolean,
  ): Promise<T> {
    let join = '';
    let qs = '';
    if (Object.keys(params).length) {
      qs = this.queryString(params);
      join = url.includes('?') ? '&' : '?';
    }

    return this.resolveResponse<T>(
      this.instance.get(url + join + qs, { ...config, data: params }),
      withHeaders,
    );
  }

  post<T = any>(url: string, params: any = {}, config: AxiosRequestConfig = {}): Promise<T> {
    return this.resolveResponse<T>(this.instance.post(url, params, config));
  }

  postFile<T = any>(url: string, file: Blob): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    const headers = {
      'content-type': 'multipart/form-data',
    };

    return this.resolveResponse<T>(this.instance.post(url, formData, headers));
  }

  put<T = any>(url: string, params: any = {}): Promise<T> {
    return this.resolveResponse<T>(this.instance.put(url, params));
  }

  patch<T = any>(url: string, params: any = {}): Promise<T> {
    return this.resolveResponse<T>(this.instance.patch(url, params));
  }

  delete(url: string, params: any = {}): AxiosPromise {
    return this.instance.delete(url, params);
  }

  queryString = (params: any): string =>
    Object.keys(params)
      .map((key) => {
        if (params[key] instanceof Array) {
          return params[key]
            .map((value: string) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
      })
      .join('&');

  cancelTokenSource = () => axios.CancelToken.source();
}

const ApiClient = new HttpClient();
export default ApiClient;
